"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faMicrophone,
  faPhone,
  faPhoneSlash,
  faRotateRight,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  SOFTPHONE_DIAL_EVENT,
  SOFTPHONE_INCOMING_EVENT,
  SOFTPHONE_WORKFLOW_EVENT,
  SOFTPHONE_WORKFLOW_RESULT_EVENT,
} from "@/components/telephony/events";
import { ModuleInfoModal } from "@/components/modules/ModuleInfoModal";
import { useDeferredStartup } from "@/hooks/useDeferredStartup";
import { fetchCallCenterEligibility } from "@/lib/callCenterEligibility";
import "@/styles/softphone.css";

type PhoneStatus =
  | "idle"
  | "connecting"
  | "registered"
  | "calling"
  | "ringing"
  | "in-call"
  | "failed";

type JsSIPModule = typeof import("jssip");
type JsSIPEvent = Record<string, unknown>;
type JsSIPSession = {
  remote_identity?: {
    uri?: { user?: string };
    display_name?: string;
  };
  connection?: RTCPeerConnection;
  on: (event: string, callback: (event: JsSIPEvent) => void) => void;
  answer?: (options: Record<string, unknown>) => void;
  terminate?: () => void;
};
type JsSIPUA = {
  on: (event: string, callback: (event: JsSIPEvent) => void) => void;
  start: () => void;
  stop?: () => void;
  isRegistered?: () => boolean;
  call: (target: string, options: Record<string, unknown>) => JsSIPSession;
};

type SoftphoneConfig = {
  enabled: boolean;
  santralActive: boolean;
  extension: string;
  username: string;
  password: string;
  displayName?: string;
};

type SoftphoneWorkflowStep = "ready" | "result" | "reached" | "callback";
type SoftphoneWorkflowMode = "softphone" | "bridge" | "robot-bridge" | "manual";
type SoftphoneWorkflow = {
  orderId: string;
  customerName?: string;
  customerPhone?: string;
  userPhone?: string;
  mode: SoftphoneWorkflowMode;
  step?: SoftphoneWorkflowStep;
};

type SoftphoneDrawerProps = {
  open: boolean;
  onClose: () => void;
  onRequestOpen?: () => void;
  variant?: "drawer" | "standalone" | "dock";
};

const SIP_DOMAIN = "panel.canerozen.com";
const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://app.canerozen.com/api";

function getToken() {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("auth_token") || "";
}

function cleanNumber(value: string) {
  let digits = String(value || "").replace(/\D+/g, "");
  if (!digits) return "";
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("90") && digits.length === 12) return digits;
  if (digits.startsWith("0") && digits.length === 11) return `9${digits}`;
  if (digits.length === 10) return `90${digits}`;
  if (digits.length > 12) return `90${digits.slice(-10)}`;
  return digits;
}

function statusLabel(status: PhoneStatus) {
  if (status === "connecting") return "Bağlanıyor";
  if (status === "registered") return "Hazır";
  if (status === "calling") return "Aranıyor";
  if (status === "ringing") return "Gelen arama";
  if (status === "in-call") return "Görüşmede";
  if (status === "failed") return "Hata";
  return "Kapalı";
}

function isActiveCallStatus(status: PhoneStatus) {
  return status === "calling" || status === "ringing" || status === "in-call";
}

export function SoftphoneDrawer({
  open,
  onClose,
  onRequestOpen,
  variant = "drawer",
}: SoftphoneDrawerProps) {
  const deferredStartupReady = useDeferredStartup(6000);
  const [status, setStatus] = useState<PhoneStatus>("idle");
  const [number, setNumber] = useState("");
  const [message, setMessage] = useState("");
  const [remoteIdentity, setRemoteIdentity] = useState("");
  const [micReady, setMicReady] = useState(false);
  const [remoteAudioReady, setRemoteAudioReady] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [showReloadConfirm, setShowReloadConfirm] = useState(false);
  const [config, setConfig] = useState<SoftphoneConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [workflow, setWorkflow] = useState<SoftphoneWorkflow | null>(null);
  const [workflowStep, setWorkflowStep] = useState<SoftphoneWorkflowStep>("ready");
  const [workflowNote, setWorkflowNote] = useState("");
  const [workflowUserPhone, setWorkflowUserPhone] = useState("");
  const [workflowSaving, setWorkflowSaving] = useState("");
  const [santralInfoOpen, setSantralInfoOpen] = useState(false);
  const uaRef = useRef<JsSIPUA | null>(null);
  const sessionRef = useRef<JsSIPSession | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const boundSessionsRef = useRef<WeakSet<JsSIPSession>>(new WeakSet());
  const allowReloadRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);

  const resetSession = useCallback(() => {
    sessionRef.current = null;
    setRemoteIdentity("");
  }, []);

  const publishIncomingState = useCallback((incoming: boolean) => {
    window.dispatchEvent(
      new CustomEvent(SOFTPHONE_INCOMING_EVENT, { detail: { incoming } }),
    );
  }, []);

  const publishWorkflowResult = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SOFTPHONE_WORKFLOW_RESULT_EVENT));
  }, []);

  const submitWorkflowResult = useCallback(async (resultType: string, note = workflowNote) => {
    if (!workflow?.orderId || workflowSaving) return;
    setWorkflowSaving(resultType);
    try {
      const response = await fetch(`${API_BASE}/panel/orders/${encodeURIComponent(workflow.orderId)}/delivery-door-approval/call-result`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ resultType, note }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) throw new Error(json?.message || "Arama sonucu kaydedilemedi");
      setWorkflow(null);
      setWorkflowStep("ready");
      setWorkflowNote("");
      publishWorkflowResult();
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Arama sonucu kaydedilemedi");
    } finally {
      setWorkflowSaving("");
    }
  }, [onClose, publishWorkflowResult, workflow?.orderId, workflowNote, workflowSaving]);

  const startWorkflowRobotBridge = useCallback(async () => {
    if (!workflow?.orderId || workflowSaving) return;
    setWorkflowSaving("robot-bridge");
    try {
      const response = await fetch(`${API_BASE}/panel/orders/${encodeURIComponent(workflow.orderId)}/delivery-door-approval/robot-callback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          userPhone: workflowUserPhone,
          customerPhone: workflow.customerPhone,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) throw new Error(json?.message || "İkili telefon birleştirme başlatılamadı");
      setWorkflowStep("result");
      publishWorkflowResult();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "İkili telefon birleştirme başlatılamadı");
    } finally {
      setWorkflowSaving("");
    }
  }, [publishWorkflowResult, workflow?.customerPhone, workflow?.orderId, workflowSaving, workflowUserPhone]);

  const playRemoteAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audio.srcObject) return;
    audio.muted = false;
    audio.volume = 1;
    audio.play().then(() => {
      setPlaybackBlocked(false);
    }).catch(() => {
      setPlaybackBlocked(true);
      setMessage("Karşı taraf sesi hazır. Sesi başlatmak için Sesi Aç'a basın.");
    });
  }, []);

  const attachRemoteAudio = useCallback((session: JsSIPSession) => {
    const attachStream = (stream?: MediaStream) => {
      if (!stream || !audioRef.current) return;
      if (!stream.getAudioTracks().length) return;
      audioRef.current.srcObject = stream;
      setRemoteAudioReady(true);
      playRemoteAudio();
    };

    const attachPeerConnection = (pc?: RTCPeerConnection) => {
      if (!pc) return;
      const stream = new MediaStream();
      pc.getReceivers().forEach((receiver) => {
        if (receiver.track?.kind === "audio") stream.addTrack(receiver.track);
      });
      attachStream(stream);
    };

    attachPeerConnection(session.connection);
    session.on("peerconnection", (event) => {
      const pc = event.peerconnection as RTCPeerConnection | undefined;
      if (!pc) return;
      pc.addEventListener("track", (trackEvent: RTCTrackEvent) => {
        const [stream] = trackEvent.streams;
        attachStream(stream);
      });
      pc.addEventListener("addstream", (streamEvent) => {
        attachStream((streamEvent as MediaStreamEvent).stream);
      });
      window.setTimeout(() => attachPeerConnection(pc), 250);
      window.setTimeout(() => attachPeerConnection(pc), 1000);
    });
    window.setTimeout(() => attachPeerConnection(session.connection), 250);
    window.setTimeout(() => attachPeerConnection(session.connection), 1000);
  }, [playRemoteAudio]);

  const bindSession = useCallback(
    (session: JsSIPSession, incoming = false) => {
      if (boundSessionsRef.current.has(session)) return;
      if (sessionRef.current && incoming) {
        setMessage("Yeni gelen arama var; mevcut çağrı ekranı açık.");
        return;
      }
      boundSessionsRef.current.add(session);
      sessionRef.current = session;
      attachRemoteAudio(session);
      setRemoteIdentity(
        session?.remote_identity?.uri?.user ||
          session?.remote_identity?.display_name ||
          "",
      );
      setStatus(incoming ? "ringing" : "calling");
      publishIncomingState(incoming);

      session.on("progress", () => {
        setStatus(incoming ? "ringing" : "calling");
        publishIncomingState(incoming);
      });
      session.on("accepted", () => {
        setStatus("in-call");
        publishIncomingState(false);
        attachRemoteAudio(session);
      });
      session.on("confirmed", () => {
        setStatus("in-call");
        publishIncomingState(false);
        attachRemoteAudio(session);
      });
      session.on("ended", () => {
        setStatus(uaRef.current?.isRegistered?.() ? "registered" : "idle");
        publishIncomingState(false);
        setRemoteAudioReady(false);
        setPlaybackBlocked(false);
        resetSession();
        if (workflow && workflowStep === "result") {
          submitWorkflowResult("ended", "Arama modalı başlatıldı, işlem bilgisi verilmedi.");
        }
      });
      session.on("failed", (event) => {
        const cause = event.cause ? `: ${String(event.cause)}` : "";
        setMessage(`Arama başarısız${cause}`);
        setStatus(uaRef.current?.isRegistered?.() ? "registered" : "failed");
        publishIncomingState(false);
        setRemoteAudioReady(false);
        setPlaybackBlocked(false);
        resetSession();
      });
    },
    [attachRemoteAudio, publishIncomingState, resetSession, submitWorkflowResult, workflow, workflowStep],
  );

  const requestMicrophone = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      stream.getTracks().forEach((track) => track.stop());
      setMicReady(true);
      return true;
    } catch (error) {
      setMicReady(false);
      setMessage(
        error instanceof Error
          ? error.message
          : "Mikrofon izni alınamadı",
      );
      return false;
    }
  }, []);

  const loadSoftphoneConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const data = await fetchCallCenterEligibility(API_BASE, getToken());
      const softphone = data?.softphone || {};
      const nextConfig = {
        enabled: Boolean(data?.canDial && softphone.enabled),
        santralActive: Boolean(data?.santralActive),
        extension: String(softphone.extension || "").trim(),
        username: String(softphone.username || "").trim(),
        password: String(softphone.password || "").trim(),
        displayName: String(softphone.displayName || "").trim(),
      };
      setConfig(nextConfig);
      if (!nextConfig.enabled) {
        setStatus("idle");
        setMessage(typeof data?.reason === "string" ? data.reason : "Softphone için kullanıcı SIP bilgileri eksik.");
      }
    } catch (error) {
      setConfig(null);
      setStatus("failed");
      setMessage(error instanceof Error ? error.message : "Telefon ayarları alınamadı");
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const connect = useCallback(async () => {
    if (!config?.enabled) return;
    if (uaRef.current) return;
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setStatus("connecting");
    setMessage("");

    try {
      const JsSIP = (await import("jssip")) as JsSIPModule;
      JsSIP.debug.disable("JsSIP:*");
      const socketUrl =
        typeof window === "undefined"
          ? "wss://panel.canerozen.com/asterisk-ws"
          : `wss://${window.location.host}/asterisk-ws`;
      const socket = new JsSIP.WebSocketInterface(socketUrl);
      const ua = new JsSIP.UA({
        sockets: [socket],
        uri: `sip:${config.extension}@${SIP_DOMAIN}`,
        authorization_user: config.username,
        password: config.password,
        display_name: config.displayName || config.extension,
        register: true,
        session_timers: false,
      });

      uaRef.current = ua as JsSIPUA;

      ua.on("connecting", () => setStatus("connecting"));
      ua.on("registered", () => {
        if (uaRef.current !== ua) return;
        if (reconnectTimerRef.current) {
          window.clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        setStatus("registered");
        setMessage("");
      });
      ua.on("registrationFailed", (event) => {
        if (uaRef.current !== ua) return;
        const cause = event.cause ? `: ${String(event.cause)}` : "";
        setStatus("failed");
        setMessage(`Dahili kayıt başarısız${cause}`);
      });
      ua.on("disconnected", () => {
        if (uaRef.current !== ua || sessionRef.current) return;
        uaRef.current = null;
        setStatus("connecting");
        setMessage("Telefon bağlantısı yenileniyor.");
        if (!reconnectTimerRef.current) {
          reconnectTimerRef.current = window.setTimeout(() => {
            reconnectTimerRef.current = null;
            connect();
          }, 1500);
        }
      });
      ua.on("newRTCSession", (event) => {
        const session = event.session as JsSIPSession | undefined;
        if (!session) return;
        bindSession(session, event.originator === "remote");
      });

      ua.start();
    } catch (error) {
      setStatus("failed");
      setMessage(error instanceof Error ? error.message : "Telefon başlatılamadı");
    }
  }, [bindSession, config]);

  useEffect(() => {
    if (!deferredStartupReady) return;
    loadSoftphoneConfig();
  }, [deferredStartupReady, loadSoftphoneConfig]);

  useEffect(() => {
    const openWorkflow = (event: Event) => {
      const detail = (event as CustomEvent<SoftphoneWorkflow>).detail || {};
      if (!detail.orderId) return;
      setWorkflow(detail);
      setWorkflowStep(detail.step || (detail.mode === "softphone" ? "result" : "ready"));
      setWorkflowNote("");
      setWorkflowUserPhone(detail.userPhone || "");
      if (detail.customerPhone) setNumber(cleanNumber(detail.customerPhone));
      onRequestOpen?.();
    };
    window.addEventListener(SOFTPHONE_WORKFLOW_EVENT, openWorkflow);
    return () => window.removeEventListener(SOFTPHONE_WORKFLOW_EVENT, openWorkflow);
  }, [onRequestOpen]);

  useEffect(() => {
    if (!configLoading && config?.enabled) connect();
  }, [config?.enabled, configLoading, connect]);

  useEffect(() => {
    const hasActiveCall = isActiveCallStatus(status);
    if (!hasActiveCall) {
      allowReloadRef.current = false;
      setShowReloadConfirm(false);
      return;
    }

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (allowReloadRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };

    const confirmKeyboardReload = (event: KeyboardEvent) => {
      const isReloadKey =
        event.key === "F5" ||
        ((event.ctrlKey || event.metaKey) &&
          event.key.toLowerCase() === "r");
      if (!isReloadKey || allowReloadRef.current) return;
      event.preventDefault();
      setShowReloadConfirm(true);
    };

    window.addEventListener("beforeunload", warnBeforeUnload);
    window.addEventListener("keydown", confirmKeyboardReload, true);
    return () => {
      window.removeEventListener("beforeunload", warnBeforeUnload);
      window.removeEventListener("keydown", confirmKeyboardReload, true);
    };
  }, [status]);

  useEffect(() => {
    return () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      try {
        sessionRef.current?.terminate?.();
        uaRef.current?.stop?.();
      } catch {
        // ignore cleanup failures
      }
    };
  }, []);

  const call = useCallback(async () => {
    const target = cleanNumber(number);
    if (!target || !uaRef.current) return;
    if (!uaRef.current.isRegistered?.()) {
      setMessage("Dahili henüz hazır değil.");
      return;
    }
    try {
      const allowed = await requestMicrophone();
      if (!allowed) return;
      const session = uaRef.current.call(`sip:${target}@${SIP_DOMAIN}`, {
        mediaConstraints: { audio: true, video: false },
        pcConfig: {
          iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
        },
        rtcOfferConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false,
        },
      });
      bindSession(session, false);
    } catch (error) {
      setStatus("failed");
      setMessage(
        error instanceof Error
          ? error.message
          : "Mikrofon izni alınamadı veya arama başlatılamadı",
      );
    }
  }, [bindSession, number, requestMicrophone]);

  const answer = useCallback(async () => {
    const session = sessionRef.current;
    if (!session) return;
    try {
      const allowed = await requestMicrophone();
      if (!allowed) return;
      session.answer({
        mediaConstraints: { audio: true, video: false },
        pcConfig: {
          iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
        },
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Çağrı cevaplanamadı");
    }
  }, [requestMicrophone]);

  const answerIncoming = useCallback(() => {
    onRequestOpen?.();
    answer();
  }, [answer, onRequestOpen]);

  const hangup = useCallback(() => {
    try {
      sessionRef.current?.terminate?.();
    } finally {
      setStatus(uaRef.current?.isRegistered?.() ? "registered" : "idle");
      publishIncomingState(false);
      resetSession();
      if (workflow && workflowStep === "result") {
        submitWorkflowResult("ended", "Arama modalı başlatıldı, işlem bilgisi verilmedi.");
      }
    }
  }, [publishIncomingState, resetSession, submitWorkflowResult, workflow, workflowStep]);

  const closeSoftphone = useCallback(() => {
    if (workflow && workflowStep === "result") {
      submitWorkflowResult("ended", "Arama modalı başlatıldı, işlem bilgisi verilmedi.");
      return;
    }
    onClose();
  }, [onClose, submitWorkflowResult, workflow, workflowStep]);

  const reconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    try {
      uaRef.current?.stop?.();
    } catch {
      // ignore stop failure
    }
    uaRef.current = null;
    resetSession();
    loadSoftphoneConfig();
  }, [loadSoftphoneConfig, resetSession]);

  const continueReload = useCallback(() => {
    allowReloadRef.current = true;
    window.location.reload();
  }, []);

  const canCall = status === "registered" && cleanNumber(number).length >= 7;
  const hasActiveSession = isActiveCallStatus(status);
  const hasIncomingCall = status === "ringing";
  const extensionLabel = config?.extension || "-";
  const canUseBridgeWorkflow = Boolean(config?.santralActive);
  const santralUnavailable = Boolean(config && !config.santralActive);

  useEffect(() => {
    const fillNumber = (event: Event) => {
      const detail = (event as CustomEvent<{ number?: string }>).detail || {};
      const nextNumber = String(detail.number || "").trim();
      if (!nextNumber || hasActiveSession) return;
      setNumber(cleanNumber(nextNumber));
      setMessage("");
      onRequestOpen?.();
    };
    window.addEventListener(SOFTPHONE_DIAL_EVENT, fillNumber);
    return () => window.removeEventListener(SOFTPHONE_DIAL_EVENT, fillNumber);
  }, [hasActiveSession, onRequestOpen]);

  if (!open && !hasIncomingCall) return null;

  return (
    <div className={`softphone softphone--${variant}`}>
      {open && (
      <div className="softphone__panel" role="dialog" aria-label="Telefon">
        <div className="softphone__header">
          <div>
            <span className="softphone__eyebrow">Farktor Telefon</span>
            <strong>Web Softphone</strong>
          </div>
          <button type="button" onClick={closeSoftphone} aria-label="Kapat">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {workflow && (
          <div className="softphone-workflow">
            <div className="softphone-workflow__head">
              <span>Kapıda ödeme araması</span>
              <strong>{workflow.customerName || workflow.customerPhone || "Müşteri"}</strong>
            </div>

            {workflow.mode === "robot-bridge" && workflowStep === "ready" && canUseBridgeWorkflow && (
              <div className="softphone-workflow__box">
                <label>
                  <span>Personel telefonu</span>
                  <input
                    value={workflowUserPhone}
                    onChange={(event) => setWorkflowUserPhone(event.target.value)}
                    placeholder="05xx veya 5xx"
                    inputMode="tel"
                  />
                </label>
                <div className="softphone-workflow__actions">
                  <button type="button" onClick={startWorkflowRobotBridge} disabled={workflowSaving !== ""}>
                    {workflowSaving === "robot-bridge" ? "Başlatılıyor" : "İkili Aramayı Başlat"}
                  </button>
                  <button
                    type="button"
                    className="is-secondary"
                    onClick={() => {
                      setWorkflow((current) => current ? { ...current, mode: "manual" } : current);
                      setWorkflowStep("result");
                    }}
                  >
                    Manuel Ara
                  </button>
                </div>
              </div>
            )}

            {workflow.mode === "robot-bridge" && workflowStep === "ready" && !canUseBridgeWorkflow && (
              <button
                type="button"
                className="softphone-workflow__notice softphone-workflow__notice--action"
                onClick={() => setSantralInfoOpen(true)}
              >
                Santral modülü mevcut değil, detaylı bilgi için tıklayın
              </button>
            )}

            {(workflow.mode === "manual" || workflowStep === "result" || (workflow.mode === "robot-bridge" && workflowStep === "ready" && !canUseBridgeWorkflow)) && (
              <div className="softphone-workflow__results">
                <button type="button" onClick={() => submitWorkflowResult("unreachable")} disabled={workflowSaving !== ""}>
                  Müşteriye Ulaşılamadı
                </button>
                <button type="button" onClick={() => setWorkflowStep("reached")} disabled={workflowSaving !== ""}>
                  Ulaştım
                </button>
                <button type="button" onClick={() => setWorkflowStep("callback")} disabled={workflowSaving !== ""}>
                  Daha Sonra Tekrar Aranacak
                </button>
              </div>
            )}

            {workflowStep === "reached" && (
              <div className="softphone-workflow__decision">
                <button type="button" className="is-success" onClick={() => submitWorkflowResult("reached_approve")} disabled={workflowSaving !== ""}>
                  Siparişi Onayla
                </button>
                <button type="button" className="is-danger" onClick={() => submitWorkflowResult("reached_cancel")} disabled={workflowSaving !== ""}>
                  Siparişi İptal Et
                </button>
              </div>
            )}

            {workflowStep === "callback" && (
              <div className="softphone-workflow__box">
                <label>
                  <span>Not</span>
                  <textarea
                    value={workflowNote}
                    onChange={(event) => setWorkflowNote(event.target.value)}
                    rows={3}
                    placeholder="İsteğe bağlı not"
                  />
                </label>
                <div className="softphone-workflow__actions">
                  <button type="button" onClick={() => submitWorkflowResult("callback")} disabled={workflowSaving !== ""}>
                    Kaydet
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!santralUnavailable && (
          <div className={`softphone__status softphone__status--${status}`}>
            <FontAwesomeIcon icon={faCircle} />
            <span>{statusLabel(status)}</span>
            <small>Dahili {extensionLabel}</small>
            <button type="button" className="softphone__status-refresh" onClick={reconnect} aria-label="Yenile">
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
          </div>
        )}

        <label className="softphone__field">
          <span>Numara</span>
          <input
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") call();
            }}
            placeholder="05xx veya 5xx"
            inputMode="tel"
            readOnly
            disabled={hasActiveSession}
          />
        </label>

        {remoteIdentity && (
          <div className="softphone__remote">Karşı taraf: {remoteIdentity}</div>
        )}

        {santralUnavailable ? (
          <button
            type="button"
            className="softphone__message softphone__message--action"
            onClick={() => setSantralInfoOpen(true)}
          >
            Santral modülü mevcut değil, detaylı bilgi için tıklayın
          </button>
        ) : (
          message && <div className="softphone__message">{message}</div>
        )}

        {remoteAudioReady && playbackBlocked && (
          <button
            type="button"
            className="softphone__unblock"
            onClick={playRemoteAudio}
          >
            Sesi Aç
          </button>
        )}

        <div className="softphone__actions">
          {status === "ringing" ? (
            <button type="button" className="softphone__call" onClick={answer}>
              <FontAwesomeIcon icon={faPhone} />
              Cevapla
            </button>
          ) : (
            <button
              type="button"
              className="softphone__call"
              onClick={call}
              disabled={!canCall}
            >
              <FontAwesomeIcon icon={faPhone} />
              Ara
            </button>
          )}
          <button
            type="button"
            className="softphone__hangup"
            onClick={hangup}
            disabled={!hasActiveSession}
          >
            <FontAwesomeIcon icon={faPhoneSlash} />
          </button>
        </div>

        <div className="softphone__hint">
          <FontAwesomeIcon icon={faMicrophone} />
          {micReady
            ? "Mikrofon izni hazır, arama panel üzerinden yapılır."
            : "Mikrofon izni bekleniyor."}
        </div>

        <audio ref={audioRef} autoPlay playsInline />
      </div>
      )}

      {hasIncomingCall && !open && (
        <div className="softphone-incoming" role="dialog" aria-label="Gelen arama">
          <div>
            <span>Gelen arama</span>
            <strong>{remoteIdentity || "Bilinmeyen numara"}</strong>
          </div>
          <div className="softphone-incoming__actions">
            <button
              type="button"
              className="softphone-incoming__answer"
              onClick={answerIncoming}
            >
              <FontAwesomeIcon icon={faPhone} />
              Cevapla
            </button>
          </div>
          <audio ref={audioRef} autoPlay playsInline />
        </div>
      )}

      {showReloadConfirm && (
        <div className="softphone-reload" role="dialog" aria-modal="true">
          <div className="softphone-reload__panel">
            <strong>Görüşme devam ediyor</strong>
            <p>
              Yenilemeye zorlarsanız mevcut görüşmeniz kesilecektir. Devam
              etmek istediğinizden emin misiniz?
            </p>
            <div className="softphone-reload__actions">
              <button
                type="button"
                className="softphone-reload__cancel"
                onClick={() => setShowReloadConfirm(false)}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className="softphone-reload__confirm"
                onClick={continueReload}
              >
                Yine de yenile
              </button>
            </div>
          </div>
        </div>
      )}

      <ModuleInfoModal
        open={santralInfoOpen}
        moduleId={353}
        onClose={() => setSantralInfoOpen(false)}
        source="softphone_santral_unavailable"
        eyebrow="Farktor Telefon"
        metadata={{
          module: "mod_353",
          title: "Santral ve Çağrı Merkezi",
          workflowMode: workflow?.mode || "",
          orderId: workflow?.orderId || "",
        }}
        defaults={{
          title: "Santral ve Çağrı Merkezi",
          introTitle: "Panel içinden arama deneyimi",
          introText: "Santral modülü aktif olduğunda personeller panelden müşteri araması başlatabilir, çağrı merkezi kullanıcıları web softphone ile görüşme yapabilir.",
          features: [
            { title: "Web softphone", text: "Yetkili personel panelden doğrudan müşteri araması yapabilir." },
            { title: "Çağrı kayıtları", text: "Arama geçmişi ve sonuçları operasyon akışına bağlı şekilde tutulur." },
            { title: "Takip edilebilir süreç", text: "Sipariş onayı ve müşteri görüşmeleri tek ekrandan yönetilir." },
          ],
        }}
      />
    </div>
  );
}
